// What the call screen shows for a given call state - pure functions, no DOM,
// so every state can be checked in a unit test (callScreenModel.test.mjs).
//
// A call moves through these phases:
//
//   outgoing:  dialing -> ringing -> connecting? -> active <-> reconnecting -> ended
//   incoming:  incoming -> connecting -> active <-> reconnecting -> ended
//
// "dialing" is the INVITE on its way; "ringing" starts with the far end's 180
// (or 183) - the moment it is really ringing somewhere. "ended" carries a
// reason, which decides the words, whether "Call again" is offered and how
// long the screen stays before closing itself.

export const PHASES = ['dialing', 'ringing', 'incoming', 'connecting', 'active', 'reconnecting', 'ended'];

// A normal ending closes the screen by itself after a moment; one that needs
// a decision (call again? open settings?) waits longer for it, but never
// forever - a screen left up on its own reads as a call still going on.
export const AUTO_CLOSE_MS = 1800;
export const AUTO_CLOSE_ERROR_MS = 15000;

const QUIET_ENDINGS = new Set(['ended', 'cancelled', 'answeredElsewhere', 'missed']);
const RETRYABLE = new Set(['busy', 'noAnswer', 'unavailable', 'failed', 'declined', 'micBusy', 'micMissing']);
const MIC_ENDINGS = new Set(['micDenied', 'micMissing', 'micBusy']);

const END_TEXT = {
    ended: 'endEnded',
    cancelled: 'endCancelled',
    declined: 'endDeclined',
    busy: 'endBusy',
    noAnswer: 'endNoAnswer',
    unavailable: 'endUnavailable',
    failed: 'endFailed',
    micDenied: 'endMicDenied',
    micMissing: 'endMicMissing',
    micBusy: 'endMicBusy',
    answeredElsewhere: 'endAnsweredElsewhere',
    missed: 'endMissed',
};

//********************* END REASONS ********************************//
// The final SIP response to our own INVITE -> why the call did not happen.
// FreeSWITCH maps its hangup causes onto these codes (NO_ANSWER -> 480,
// USER_BUSY -> 486, CALL_REJECTED -> 603, NO_USER_RESPONSE -> 408,
// UNALLOCATED_NUMBER/USER_NOT_REGISTERED -> 404, NORMAL_TEMPORARY_FAILURE -> 503).
export function endReasonFromSipStatus(statusCode) {
    const code = Number(statusCode);
    if(!code) return 'failed';
    if(code === 486 || code === 600) return 'busy';
    if(code === 603) return 'declined';
    if(code === 408 || code === 480 || code === 487) return 'noAnswer';
    if(code === 404 || code === 410 || code === 484 || code === 604) return 'unavailable';
    return 'failed';
}

// getUserMedia's error -> the microphone reason, or null for anything else.
export function endReasonFromMediaError(error) {
    const name = error?.name || '';
    if(name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') return 'micDenied';
    if(name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') return 'micMissing';
    if(name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') return 'micBusy';
    return null;
}

// The native call engines (CallEngine.swift / CallEngine.java) send endReason
// from this version on; older builds send only a free-text reason, read here
// as well as it can be.
export function endReasonFromNative(call) {
    switch (call?.endReason) {
        case 'hungUp': return 'ended';
        case 'declined': return 'ended';
        case 'remote': return 'ended';
        case 'unanswered': return 'missed';
        case 'answeredElsewhere': return 'answeredElsewhere';
        case 'busy': return 'ended';
        case 'failed': return 'failed';
        default: break;
    }

    const reason = String(call?.reason || '').toLowerCase();
    if(reason.includes('answered elsewhere')) return 'answeredElsewhere';
    if(reason.includes('not answered') || reason.includes('caller hung up')) return 'missed';
    if(reason.includes('fail') || reason.includes('no audio') || reason.includes('cannot')) return 'failed';
    return 'ended';
}

//********************* FORMATTING ********************************//
export function formatDuration(ms) {
    const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const pad = (value) => String(value).padStart(2, '0');
    return hours ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

// Two letters for the avatar. A phone number has none worth showing - the
// avatar shows a person glyph instead.
export function initialsOf(name) {
    const text = String(name || '').trim();
    if(!text || /^[+\d(]/.test(text)) return '';
    const words = text.split(/\s+/).filter(Boolean);
    const letters = words.length > 1
        ? [words[0], words[words.length - 1]].map((word) => Array.from(word)[0])
        : Array.from(words[0] || '').slice(0, 2);
    return letters.join('').toUpperCase();
}

// A stable hue per person, which tints the avatar and the glow behind it.
// Drawn from blues, greens and violets only: red belongs to the hang-up
// button, and a red-tinted call would read as an error.
const HUES = [212, 194, 172, 152, 236, 258, 282, 318];

export function hueOf(name) {
    const text = String(name || '');
    let hash = 0;
    for(let index = 0; index < text.length; index++) {
        hash = (hash * 31 + text.charCodeAt(index)) | 0;
    }
    return HUES[Math.abs(hash) % HUES.length];
}

//********************* AUDIO ROUTES ********************************//
// A plain speaker toggle when the phone has only its own earpiece and
// speaker; a list to choose from once a headset or Bluetooth device is there.
export function audioControlOf(audio, t) {
    if(!audio || !Array.isArray(audio.routes) || !audio.routes.length) return null;

    const routes = audio.routes;
    const external = routes.some((route) => route.type === 'bluetooth' || route.type === 'headset');
    const current = routes.find((route) => route.id && route.id === audio.activeId)
        || routes.find((route) => route.type === audio.route)
        || null;

    if(!external) {
        // A speaker toggle needs both ends: a tablet has no earpiece to
        // switch back to.
        const hasBoth = routes.some((route) => route.type === 'speaker') && routes.some((route) => route.type === 'earpiece');
        if(!hasBoth) return null;
        return {
            mode: 'toggle',
            on: audio.route === 'speaker',
            icon: 'speaker',
            label: t('speaker'),
            pending: Boolean(audio.pending),
        };
    }

    return {
        mode: 'menu',
        on: audio.route !== 'earpiece',
        icon: current?.type === 'bluetooth' ? 'bluetooth' : current?.type === 'headset' ? 'headphones' : current?.type === 'earpiece' ? 'earpiece' : 'speaker',
        label: current ? routeLabelOf(current, t) : t('speaker'),
        pending: Boolean(audio.pending),
    };
}

export function routeLabelOf(route, t) {
    if(route.type === 'earpiece') return t('routeEarpiece');
    if(route.type === 'speaker') return t('routeSpeaker');
    const name = String(route.name || '').trim();
    if(name) return name;
    return route.type === 'headset' ? t('routeHeadset') : t('routeBluetooth');
}

//********************* DESCRIBE ********************************//
// state: {
//   phase, direction: 'outgoing'|'incoming', video: { incoming },
//   connectedAt, endedAt, end: { reason }, canRetry, canOpenSettings, inApp
// }
export function describeCall(state, t, now = Date.now()) {
    const phase = PHASES.includes(state.phase) ? state.phase : 'connecting';
    const view = {
        phase,
        status: '',
        detail: '',
        tone: 'neutral',
        halo: 'none',
        dock: 'call',
        showControls: true,
        showTimer: false,
        retry: false,
        settings: false,
        autoCloseMs: null,
    };

    switch (phase) {
        case 'dialing':
            view.status = t('statusDialing');
            view.halo = 'out';
            break;
        case 'ringing':
            view.status = t('statusRinging');
            view.halo = 'out';
            break;
        case 'incoming':
            view.status = state.video?.incoming ? t('statusIncomingVideo') : t('statusIncoming');
            view.halo = 'in';
            view.dock = 'incoming';
            view.showControls = false;
            break;
        case 'connecting':
            view.status = t('statusConnecting');
            view.halo = 'spin';
            break;
        case 'active':
            view.status = formatDuration(now - (state.connectedAt || now));
            view.showTimer = true;
            view.halo = 'still';
            break;
        case 'reconnecting':
            view.status = t('statusReconnecting');
            view.tone = 'warn';
            view.halo = 'spin';
            break;
        case 'ended': {
            const reason = END_TEXT[state.end?.reason] ? state.end.reason : 'ended';
            // A call that failed after it had connected dropped, rather than
            // never got through.
            const dropped = reason === 'failed' && Boolean(state.connectedAt);
            view.status = t(dropped ? 'endDropped' : END_TEXT[reason]);
            view.dock = 'ended';
            view.showControls = false;
            view.halo = 'none';
            view.tone = QUIET_ENDINGS.has(reason) ? 'neutral' : 'error';

            if(state.connectedAt && (reason === 'ended' || reason === 'failed')) {
                view.detail = formatDuration((state.endedAt || now) - state.connectedAt);
            } else if(reason === 'failed') {
                view.detail = t('detailFailed');
            } else if(reason === 'micDenied') {
                view.detail = state.inApp ? t('detailMicDeniedApp') : t('detailMicDeniedBrowser');
            } else if(reason === 'micMissing') {
                view.detail = t('detailMicMissing');
            } else if(reason === 'micBusy') {
                view.detail = t('detailMicBusy');
            }

            // "failed" includes a call that dropped midway: calling back is
            // the obvious next step there too.
            view.retry = state.direction === 'outgoing' && Boolean(state.canRetry) && RETRYABLE.has(reason);
            view.settings = reason === 'micDenied' && Boolean(state.canOpenSettings);
            view.autoCloseMs = QUIET_ENDINGS.has(reason) && !MIC_ENDINGS.has(reason) ? AUTO_CLOSE_MS : AUTO_CLOSE_ERROR_MS;
            break;
        }
        default:
            break;
    }

    return view;
}
